import { useCallback, useEffect, useMemo, useRef } from 'react';

import { DEFAULT_CREDITS_ATTEMPTS, MINIMUM_POINTS_TO_PLAY } from '../prize-box-page.constants';
import { GamePhase, PrizeKind } from '../prize-box-page.types';
import { mapMoveToHand } from '../prize-box-page.utils';
import * as api from '../services/raffle-adapter';
import { IPrizeBoxState, usePrizeBoxStore } from '../store/prize-box-store';

import { MovesEnum } from 'shared/constants/shared-enums';
import { useAccount, usePublicClient, useWalletClient } from 'shared/hooks/use-web3';
import { useQuestsStore } from 'shared/store/questsStore';
import { isMocked } from 'shared/utils/isMocked';

import { useRestoreAllowance } from './use-restore-allowance';

export const usePrizeBoxController = () => {
  const { address } = useAccount();
  const walletClient = useWalletClient();
  const publicClient = usePublicClient();
  const chainId = publicClient?.chain?.id ?? null;
  const { needsAllowance, loading: loadingApprove } = useRestoreAllowance(publicClient, address);
  const prevChainIdRef = useRef<number | null>(null);

  const {
    extraCredits,
    attemptsLeft,
    needsRestore,
    quotaResetAt,
    isPrizeBoxLoading,
    prizeBoxError,
    poolUsdt,
    selectedMove,
    phase,
    aiMove,
    result,
    playerWins,
    aiWins,
    currentSlotBalance,
    lastMoves,
    lastRoundOutcome,
    recoveryAmount,
    pool,
    refreshPrizeBoxStore,
    setPrizeBoxStore,
    handleSmash,
  } = usePrizeBoxStore();

  const { pointBalance, bestTierPerMove, snapshotPoints } = useQuestsStore();

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const patch = useCallback((u: Partial<IPrizeBoxState & { attempts: number }>) => {
    if (!mountedRef.current) return;
    setPrizeBoxStore(u);
  }, []);

  // canPlayNow: ignore attempts when shouldRestore === false
  const canPlayNow = useMemo(
    () => pointBalance >= MINIMUM_POINTS_TO_PLAY && (needsRestore === false || attemptsLeft > 0),
    [pointBalance, attemptsLeft, needsRestore],
  );

  const selectedMoveTopHand = useMemo(
    () => (selectedMove != null ? (bestTierPerMove?.[selectedMove] ?? null) : null),
    [selectedMove, bestTierPerMove],
  );

  useEffect(() => {
    if (!quotaResetAt) return;

    const applyResetNow = () => {
      patch({ attempts: DEFAULT_CREDITS_ATTEMPTS });
      if (phase === GamePhase.Cooldown && pointBalance >= MINIMUM_POINTS_TO_PLAY) {
        patch({ phase: GamePhase.Idle });
      }
      void refreshPrizeBoxStore(publicClient, address);
    };
    const now = Date.now();
    if (now >= quotaResetAt) {
      applyResetNow();
      return;
    }
    const timeout = window.setTimeout(applyResetNow, quotaResetAt - now);
    return () => window.clearTimeout(timeout);
  }, [quotaResetAt, phase, pointBalance, patch, refreshPrizeBoxStore]);

  useEffect(() => {
    if (prevChainIdRef.current === null) {
      prevChainIdRef.current = chainId;
      return;
    }
    if (prevChainIdRef.current !== chainId) {
      prevChainIdRef.current = chainId;

      patch({
        phase: GamePhase.Idle,
        result: null,
        selectedMove: null,
        aiMove: null,
        lastMoves: null,
        lastRoundOutcome: null,
        playerWins: 0,
        aiWins: 0,
        prizeBoxError: null,
      });

      void refreshPrizeBoxStore(publicClient, address);
    }
  }, [chainId, patch, refreshPrizeBoxStore, address]);

  const handleSelect = useCallback(
    async (move: MovesEnum) => {
      if (phase === GamePhase.Revealing) return;
      if (!canPlayNow) return;
      if (!address) return;
      patch({ phase: GamePhase.Selecting, selectedMove: move });

      handleSmash(mapMoveToHand(move), publicClient, walletClient, address, canPlayNow);
    },
    [phase, address, canPlayNow, patch, publicClient, walletClient, pointBalance],
  );

  const closeResult = useCallback(() => {
    const nextPhase =
      pointBalance >= MINIMUM_POINTS_TO_PLAY && (needsRestore === false || attemptsLeft > 0)
        ? GamePhase.Idle
        : GamePhase.Cooldown;

    patch({ result: null, selectedMove: null, aiMove: null, phase: nextPhase });
    if (nextPhase === GamePhase.Idle) void refreshPrizeBoxStore(publicClient, address);
  }, [pointBalance, attemptsLeft, needsRestore, patch, refreshPrizeBoxStore]);

  // keep local response for restore; no extra refetch here
  const buyAttempts = useCallback(async () => {
    if (!address || !publicClient || !walletClient) return;
    patch({ prizeBoxError: null });
    try {
      const { extraCredits, poolUsdt, quotaResetAt, attempts } = await api.restoreAttempts(
        publicClient,
        walletClient,
        address,
      );
      patch({
        extraCredits,
        pool: poolUsdt,
        quotaResetAt,
        attempts,
        phase: GamePhase.Idle,
      });
    } catch (e: unknown) {
      const msg = String((e as Error)?.message ?? e ?? '');

      if (msg.includes('User rejected')) {
        patch({ prizeBoxError: 'User rejected the transaction.' });
        return;
      }
      if (msg.includes('InsufficientTokenBalance')) {
        patch({
          prizeBoxError:
            'Insufficient wallet balance to buy a chest. Please top up your USDT and try again.',
        });
        return;
      }
      if (msg.includes('InsufficientGasForRestore')) {
        patch({
          prizeBoxError:
            'Not enough gas to complete the purchase. Please add a bit more native token and retry.',
        });
        return;
      }

      patch({ prizeBoxError: 'Purchase failed: ' + msg });
    }
  }, [address, publicClient, walletClient, patch]);

  useEffect(() => {
    // for Visual Testing only
    if (!isMocked || typeof window === 'undefined') return;
    const m = window.__PB_MOCK;
    if (!m) return;

    if (typeof m.poolCents === 'number') patch({ pool: m.poolCents });
    if (typeof m.attempts === 'number') {
      const a = Math.max(0, m.attempts | 0);
      patch({
        attempts: a,
        needsRestore: false,
        phase: GamePhase.Idle,
      });
    }

    if (typeof m.quotaResetAt === 'number') patch({ quotaResetAt: m.quotaResetAt });
    if (typeof m.extraCredits === 'number') patch({ extraCredits: m.extraCredits });
    if (typeof m.recoveryAmount === 'number') patch({ recoveryAmount: m.recoveryAmount });

    // Force a specific RoundResult using the ENUM, not strings
    if (m.money) {
      patch({
        phase: GamePhase.Result,
        result: { kind: PrizeKind.Money, amount: m.money.amount, totalAmount: m.money.totalAmount },
      } as any);
      return;
    }
    if (m.points) {
      patch({
        phase: GamePhase.Result,
        result: { kind: PrizeKind.Points, amount: m.points.amount },
      } as any);
      return;
    }
    if (m.draw) {
      patch({
        phase: GamePhase.Result,
        result: { kind: PrizeKind.Draw },
      } as any);
      return;
    }
    if (m.lose) {
      patch({
        phase: GamePhase.Result,
        result: { kind: PrizeKind.Lose },
      } as any);
      return;
    }
  }, [patch]);

  return {
    address,
    loading: isPrizeBoxLoading,
    error: prizeBoxError,
    phase: phase,
    selectedMove: selectedMove,
    aiMove: aiMove,
    topHands: bestTierPerMove,
    selectedMoveTopHand,
    result: result,
    pool: poolUsdt,
    points: pointBalance,
    quotaResetAt: quotaResetAt,
    extraCredits: extraCredits,
    snapshotPoints: snapshotPoints,
    playerWins: playerWins,
    aiWins: aiWins,
    currentSlotBalance: currentSlotBalance,
    lastMoves: lastMoves,
    lastRoundOutcome: lastRoundOutcome,
    recoveryAmount: recoveryAmount,
    attemptsLeft,
    canPlayNow,
    handleSelect,
    closeResult,
    buyAttempts,
    setPhase: (p: GamePhase) => patch({ phase: p }),
    loadingApprove,
    needsAllowance,
    poolBalance: pool,
    refreshPrizeBoxStore,
    needsRestore: needsRestore,
  } as const;
};
